import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    input,
    computed,
    output,
    inject
} from '@angular/core';
import { Route, Router, RouterLink, RouterLinkActive } from '@angular/router';

export type LinkKind = 'router' | 'external' | 'plain';

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'ng-smart-link',
    imports: [RouterLink, RouterLinkActive, NgTemplateOutlet],
    host: { style: 'display: contents' },
    template: `
        <ng-template #content>
            <ng-content />
        </ng-template>
        @switch (kind()) {
            @case ('router') {
                <a
                    [attr.id]="elementId()"
                    [routerLink]="routePath()"
                    [fragment]="routeFragment()"
                    [queryParams]="routeQueryParams()"
                    routerLinkActive="isSelected"
                    [class]="cssClass()"
                    [attr.target]="newTab() ? '_blank' : null"
                    (click)="clicked.emit()"
                >
                    <ng-container [ngTemplateOutlet]="content" />
                </a>
            }
            @case ('external') {
                <a
                    [attr.id]="elementId()"
                    [href]="link()"
                    [class]="cssClass()"
                    [class.isSelected]="isSelected()"
                    [attr.target]="newTab() ? '_blank' : null"
                    [attr.rel]="newTab() ? 'noopener noreferrer' : null"
                    (click)="clicked.emit()"
                >
                    <ng-container [ngTemplateOutlet]="content" />
                </a>
            }
            @default {
                <a
                    [attr.id]="elementId()"
                    [href]="link()"
                    [class]="cssClass()"
                    [class.isSelected]="isSelected()"
                    [attr.target]="newTab() ? '_blank' : null"
                    (click)="clicked.emit()"
                >
                    <ng-container [ngTemplateOutlet]="content" />
                </a>
            }
        }
    `
})
export class SmartLinkComponent {
    private static readonly DUMMY_BASE = 'https://placeholder.invalid';

    readonly link = input.required<string>();
    readonly cssClass = input<string>('');
    readonly newTab = input<boolean | undefined>();
    readonly useRouter = input<boolean | undefined>(false);
    readonly isSelected = input<boolean | undefined>();
    readonly elementId = input<string>();
    readonly clicked = output<void>();

    private readonly router = inject(Router);
    private readonly parsed = computed(() => SmartLinkComponent.tryParseUrl(this.link()));

    readonly kind = computed<LinkKind>(() =>
        this.useRouter()
            ? 'router'
            : SmartLinkComponent.classifyLink(
                  this.link(),
                  SmartLinkComponent.collectRoutePaths(this.router.config)
              )
    );

    readonly routePath = computed(() => this.parsed().pathname);

    readonly routeFragment = computed(() => {
        const hash = this.parsed().hash;
        return hash ? hash.slice(1) : undefined;
    });

    readonly routeQueryParams = computed(() => {
        const params: Record<string, string> = {};
        let hasAny = false;
        this.parsed().searchParams.forEach((value, key) => {
            params[key] = value;
            hasAny = true;
        });
        return hasAny ? params : undefined;
    });

    static tryParseUrl(url: string): URL {
        try {
            return new URL(url, SmartLinkComponent.DUMMY_BASE);
        } catch {
            return new URL('/', SmartLinkComponent.DUMMY_BASE);
        }
    }

    static classifyLink(link: string, routePaths: string[]): LinkKind {
        if (link.startsWith('http')) {
            return 'external';
        }
        if (link.includes('#!/')) {
            return 'plain';
        }
        const pathWithoutLeadingSlash = SmartLinkComponent.tryParseUrl(
            link
        ).pathname.replace(/^\//, '');
        return routePaths.includes(pathWithoutLeadingSlash) ? 'router' : 'plain';
    }

    static collectRoutePaths(routes: readonly Route[]): string[] {
        return SmartLinkComponent.flattenPaths(routes, '');
    }

    private static flattenPaths(routes: readonly Route[], prefix: string): string[] {
        return routes.flatMap((route) => {
            if (route.path === '**') {
                return [];
            }
            const joined = [prefix, route.path ?? ''].filter(Boolean).join('/');
            return [
                joined,
                ...SmartLinkComponent.flattenPaths(route.children ?? [], joined)
            ];
        });
    }
}
